import { useEffect, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertCircle, Users, Award, TrendingUp, TrendingDown, Minus,
  RefreshCw, Activity, ChevronRight, Clock, CheckCircle2, AlertOctagon, Timer
} from "lucide-react";
import { dashboardService, type TeacherStats, type Alert, type MonitoredStudent } from "@/services/dashboardService";
import { useTeacherNavigation, type TeacherPage } from "@/contexts/TeacherNavigationContext";
import { useTeacherTheme } from "@/contexts/TeacherThemeContext";
import { useApp } from "@/App";
import { safeLog } from '@/utils/safeLog';


// ── 24-hour heatmap ──────────────────────────────────────────────────────────
function ActivityHeatmap({ alerts, dm }: { alerts: Alert[]; dm: boolean }) {
  // Group alerts into 2-hour buckets for the past 24h
  const fmtHour = (d: Date) => {
    const h = d.getHours();
    return h === 0 ? '12am' : h < 12 ? `${h}am` : h === 12 ? '12pm' : `${h - 12}pm`;
  };

  const buckets = Array.from({ length: 12 }, (_, i) => {
    const bucketStart = Date.now() - (12 - i) * 2 * 60 * 60 * 1000;
    const bucketEnd = bucketStart + 2 * 60 * 60 * 1000;
    const count = alerts.filter(a => {
      const t = new Date(a.createdAt).getTime();
      return t >= bucketStart && t < bucketEnd;
    }).length;
    const startDate = new Date(bucketStart);
    const endDate = new Date(bucketEnd);
    const label = fmtHour(startDate);
    const fullLabel = `${fmtHour(startDate)}–${fmtHour(endDate)}`;
    return { label, fullLabel, count };
  });

  const max = Math.max(...buckets.map(b => b.count), 1);

  return (
    <div>
      <div className="flex items-end gap-1 h-14">
        {buckets.map((b, i) => (
          <div key={i} className="flex-1 flex flex-col items-center justify-end gap-0.5 group relative">
            <div
              className={`w-full rounded-t transition-all ${b.count === 0 ? (dm ? 'bg-slate-700' : 'bg-slate-100') : b.count >= max * 0.7 ? 'bg-red-400' : b.count >= max * 0.3 ? 'bg-orange-300' : 'bg-blue-300'}`}
              style={{ height: `${Math.max(4, (b.count / max) * 52)}px` }}
            />
            {/* Tooltip */}
            <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 hidden group-hover:block bg-slate-800 text-white text-xs rounded px-1.5 py-0.5 whitespace-nowrap z-10">
              {b.fullLabel} — {b.count} alert{b.count !== 1 ? 's' : ''}
            </div>
          </div>
        ))}
      </div>
      {/* Per-bucket time labels, every other bucket to avoid crowding */}
      <div className="flex items-start gap-1 mt-1.5">
        {buckets.map((b, i) => (
          <div key={i} className="flex-1 flex justify-center">
            <span className={`text-[10px] leading-tight ${i % 2 !== 0 ? 'invisible' : ''} ${dm ? 'text-slate-500' : 'text-slate-400'}`}>
              {b.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Delta badge ──────────────────────────────────────────────────────────────
function Delta({ value, inverse = false }: { value: number; inverse?: boolean }) {
  if (value === 0) return (
    <span className="inline-flex items-center gap-0.5 text-xs text-slate-400">
      <Minus className="h-3 w-3" /> same as yesterday
    </span>
  );
  const positive = inverse ? value < 0 : value > 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${positive ? 'text-emerald-600' : 'text-red-500'}`}>
      {value > 0
        ? <TrendingUp className="h-3 w-3" />
        : <TrendingDown className="h-3 w-3" />}
      {value > 0 ? '+' : ''}{value} vs yesterday
    </span>
  );
}

export function TeacherHome() {
  const { setCurrentPage } = useTeacherNavigation();
  const { user } = useApp();
  const { dm } = useTeacherTheme();
  const [stats, setStats] = useState<TeacherStats>({
    studentsMonitored: 0,
    activeAlerts: 0,
    incidentsPrevented: 0,
    avgResponseTime: 0,
  });
  const [recentAlerts, setRecentAlerts] = useState<Alert[]>([]);
  // Larger alert set used for heatmap + delta calculations
  const [allAlerts, setAllAlerts] = useState<Alert[]>([]);
  const [atRiskStudents, setAtRiskStudents] = useState<MonitoredStudent[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsData, alertsData, allAlertsData, studentsData] = await Promise.all([
        dashboardService.getTeacherStats(),
        dashboardService.getTeacherAlerts(1, 6),
        dashboardService.getTeacherAlerts(1, 100), // for heatmap + delta
        dashboardService.getMonitoredStudents(1, 20),
      ]);
      setStats(statsData);
      setRecentAlerts(alertsData.data);
      setAllAlerts(allAlertsData.data);
      const sorted = [...studentsData.data].sort((a, b) => b.riskScore - a.riskScore).slice(0, 5);
      setAtRiskStudents(sorted);
      setLastUpdated(new Date());
    } catch (error) {
      safeLog.error("Failed to load dashboard data", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Delta helpers ──────────────────────────────────────────────────────────
  const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
  const yesterdayStart = new Date(todayStart); yesterdayStart.setDate(yesterdayStart.getDate() - 1);

  const alertsToday = allAlerts.filter(a => new Date(a.createdAt) >= todayStart).length;
  const alertsYesterday = allAlerts.filter(a => {
    const d = new Date(a.createdAt);
    return d >= yesterdayStart && d < todayStart;
  }).length;
  const alertDelta = alertsToday - alertsYesterday;

  // ── Formatters ─────────────────────────────────────────────────────────────
  const formatTimeAgo = (dateString: string) => {
    try {
      const seconds = Math.floor((Date.now() - new Date(dateString).getTime()) / 1000);
      if (seconds < 60) return 'Just now';
      const minutes = Math.floor(seconds / 60);
      if (minutes < 60) return `${minutes}m ago`;
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `${hours}h ago`;
      return `${Math.floor(hours / 24)}d ago`;
    } catch { return 'Unknown'; }
  };

  const formatResponseTime = (minutes: number) => {
    if (minutes === 0) return '—';
    if (minutes < 60) return `${minutes}m`;
    return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
  };

  const severityBadge = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-700 border border-red-200';
      case 'high': return 'bg-orange-100 text-orange-700 border border-orange-200';
      case 'medium': return 'bg-amber-100 text-amber-700 border border-amber-200';
      default: return 'bg-blue-100 text-blue-700 border border-blue-200';
    }
  };

  const severityBarColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-500';
      case 'high': return 'bg-orange-500';
      case 'medium': return 'bg-amber-400';
      default: return 'bg-blue-400';
    }
  };

  const riskColor = (score: number) => {
    if (score >= 70) return { bar: 'bg-red-500', text: 'text-red-600', bg: 'bg-red-50', label: 'High Risk', trend: 'up' };
    if (score >= 40) return { bar: 'bg-amber-400', text: 'text-amber-600', bg: 'bg-amber-50', label: 'Medium', trend: 'stable' };
    return { bar: 'bg-emerald-400', text: 'text-emerald-600', bg: 'bg-emerald-50', label: 'Low', trend: 'down' };
  };

  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  const greeting = new Date().getHours() < 12 ? 'Good morning' : new Date().getHours() < 17 ? 'Good afternoon' : 'Good evening';

  // ── Top alert category per student (from recentAlerts cross-ref) ───────────
  const studentAlertCounts = (studentName: string) => {
    const matches = allAlerts.filter(a => a.studentName === studentName);
    if (matches.length === 0) return null;
    const typeCount: Record<string, number> = {};
    matches.forEach(a => { typeCount[a.alertType] = (typeCount[a.alertType] || 0) + 1; });
    const top = Object.entries(typeCount).sort((a, b) => b[1] - a[1])[0];
    return top ? `${top[1]}× ${top[0].replace(/_/g, ' ')}` : null;
  };

  const statCards = [
    {
      title: 'Active Alerts',
      value: stats.activeAlerts,
      icon: AlertOctagon,
      iconColor: 'text-red-500',
      bgColor: 'bg-red-50',
      borderColor: 'border-l-red-500',
      delta: <Delta value={alertDelta} inverse={true} />,
      urgent: stats.activeAlerts > 0,
    },
    {
      title: 'Students Monitored',
      value: stats.studentsMonitored,
      icon: Users,
      iconColor: 'text-emerald-500',
      bgColor: 'bg-emerald-50',
      borderColor: 'border-l-emerald-500',
      delta: null,
      urgent: false,
    },
    {
      title: 'Incidents Resolved',
      value: stats.incidentsPrevented,
      icon: CheckCircle2,
      iconColor: 'text-blue-500',
      bgColor: 'bg-blue-50',
      borderColor: 'border-l-blue-500',
      delta: null,
      urgent: false,
    },
    {
      title: 'Avg Response Time',
      value: formatResponseTime(stats.avgResponseTime),
      icon: Timer,
      iconColor: 'text-violet-500',
      bgColor: 'bg-violet-50',
      borderColor: 'border-l-violet-500',
      delta: null,
      urgent: false,
    },
  ];

  return (
    <div className="space-y-6">

      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className={`text-xl font-bold ${dm ? 'text-slate-100' : 'text-slate-800'}`}>{greeting}, {user?.name?.split(' ')[0] || 'Teacher'}</h2>
          <p className={`text-sm mt-0.5 ${dm ? 'text-slate-400' : 'text-slate-500'}`}>{today}</p>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="btn-glass btn-glass-pill flex items-center gap-2"
        >
          <RefreshCw style={{ width: '0.75rem', height: '0.75rem' }} className={loading ? 'animate-spin' : ''} />
          {lastUpdated ? `Updated ${lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : 'Refresh'}
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        {statCards.map((card) => {
          // Determine navigation target based on card title
          const getNavigationTarget = (title: string) => {
            switch (title) {
              case 'Active Alerts':
                return 'alerts';
              case 'Students Monitored':
                return 'studentsMonitored';
              case 'Incidents Resolved':
                return 'reports';
              default:
                return null;
            }
          };

          const navTarget = getNavigationTarget(card.title);

          return (
            <Card
              key={card.title}
              className={`border-0 shadow-sm hover:shadow-md transition border-l-4 ${card.borderColor} ${card.urgent ? 'ring-1 ring-red-200' : ''} ${dm ? 'bg-slate-800' : 'bg-white'} ${navTarget ? 'cursor-pointer hover:scale-105' : ''}`}
              onClick={() => navTarget && setCurrentPage(navTarget as TeacherPage)}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className={`text-xs font-medium uppercase tracking-wide ${dm ? 'text-slate-400' : 'text-slate-500'}`}>{card.title}</p>
                    <p className={`text-3xl font-bold mt-1 leading-none ${dm ? 'text-slate-100' : 'text-slate-800'}`}>
                      {loading ? <span className={dm ? 'text-slate-600' : 'text-slate-300'}>—</span> : card.value}
                    </p>
                    <div className="mt-1.5">
                      {!loading && card.delta}
                    </div>
                  </div>
                  <div className={`${card.bgColor} rounded-xl p-2.5 shrink-0`}>
                    <card.icon className={`h-5 w-5 ${card.iconColor}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Main Content Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

        {/* Recent Alerts — spans 2 cols */}
        <Card className={`lg:col-span-2 border-0 shadow-sm ${dm ? 'bg-slate-800' : 'bg-white'}`}>
          <CardHeader className="pb-2 pt-5 px-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-red-500" />
                <CardTitle className={`text-sm font-semibold ${dm ? 'text-slate-100' : 'text-slate-800'}`}>Active Alerts</CardTitle>
                {!loading && allAlerts.length > 0 && (
                  <span className="text-xs bg-red-100 text-red-600 font-semibold px-1.5 py-0.5 rounded-full">
                    {allAlerts.length}
                  </span>
                )}
              </div>
              <div
                onClick={() => setCurrentPage('activeAlerts')}
                className="flex items-center gap-1 text-xs font-medium transition cursor-pointer"
                style={{
                  color: '#6b7280'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#4b5563';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '#6b7280';
                }}
              >
                View All <ChevronRight className="h-3 w-3" style={{ color: '#6b7280' }} />
              </div>
            </div>
          </CardHeader>
          <CardContent className="px-5 pb-5 pt-0">
            <div className={`divide-y ${dm ? 'divide-slate-700' : 'divide-slate-100'}`}>
              {loading ? (
                <div className="flex items-center justify-center py-10 text-slate-400">
                  <RefreshCw className="animate-spin h-4 w-4 mr-2" />
                  <span className="text-sm">Loading alerts...</span>
                </div>
              ) : recentAlerts.length === 0 ? (
                <div className="flex flex-col items-center gap-2 py-10 text-slate-400">
                  <Shield className="h-8 w-8 text-emerald-300" />
                  <p className="text-sm font-medium">All clear — no active alerts</p>
                  <p className="text-xs text-slate-400">Your students are safe right now</p>
                </div>
              ) : (
                recentAlerts.map((alert) => (
                  <div
                    key={alert.id}
                    className={`flex items-center gap-3 py-3 cursor-pointer -mx-5 px-5 transition group ${dm ? 'hover:bg-slate-700/40' : 'hover:bg-slate-50'}`}
                    onClick={() => setCurrentPage('activeAlerts')}
                  >
                    <div className={`w-1 self-stretch rounded-full shrink-0 ${severityBarColor(alert.severity)}`} />
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-semibold truncate ${dm ? 'text-slate-200' : 'text-slate-700'}`}>{alert.studentName}</p>
                      <p className={`text-xs truncate capitalize ${dm ? 'text-slate-400' : 'text-slate-500'}`}>
                        {alert.alertType?.replace(/_/g, ' ')}
                        {alert.description ? ` — ${alert.description}` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize ${severityBadge(alert.severity)}`}>
                        {alert.severity}
                      </span>
                      <span className={`text-xs hidden sm:flex items-center gap-1 ${dm ? 'text-slate-500' : 'text-slate-400'}`}>
                        <Clock className="h-3 w-3" />
                        {formatTimeAgo(alert.createdAt)}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className={`border-0 shadow-sm ${dm ? 'bg-slate-800' : 'bg-white'}`}>
          <CardHeader className="pb-2 pt-5 px-5">
            <div className="flex items-center gap-2">
              <Activity className={`h-4 w-4 ${dm ? 'text-slate-400' : 'text-slate-500'}`} />
              <CardTitle className={`text-sm font-semibold ${dm ? 'text-slate-100' : 'text-slate-800'}`}>Quick Actions</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="px-5 pb-5 pt-1 space-y-1">
            {[
              {
                label: 'Review Alerts',
                sub: `${stats.activeAlerts} pending`,
                icon: AlertOctagon,
                iconBg: 'bg-red-50',
                iconColor: 'text-red-500',
                page: 'activeAlerts' as const,
                urgent: stats.activeAlerts > 0,
              },
              {
                label: 'View Students',
                sub: `${stats.studentsMonitored} monitored`,
                icon: Users,
                iconBg: 'bg-emerald-50',
                iconColor: 'text-emerald-500',
                page: 'studentsMonitored' as const,
                urgent: false,
              },
              {
                label: 'Gamification',
                sub: 'Class leaderboard',
                icon: Award,
                iconBg: 'bg-amber-50',
                iconColor: 'text-amber-500',
                page: 'gamificationMVP' as const,
                urgent: false,
              },
              {
                label: 'Notifications',
                sub: 'Recent updates',
                icon: TrendingUp,
                iconBg: 'bg-blue-50',
                iconColor: 'text-blue-500',
                page: 'notifications' as const,
                urgent: false,
              },
            ].map((action) => (
              <button
                key={action.label}
                onClick={() => setCurrentPage(action.page)}
                className={`w-full flex items-center gap-3 p-3 rounded-xl transition text-left ${
                  action.urgent
                    ? dm ? 'hover:bg-red-950/50' : 'hover:bg-red-50'
                    : dm ? 'hover:bg-slate-700/70' : 'hover:bg-slate-100/80'
                }`}
                style={{
                  cursor: 'pointer',
                  border: action.urgent
                    ? dm ? '1px solid rgba(127,29,29,0.4)' : '1px solid #fee2e2'
                    : dm ? '1px solid rgba(51,65,85,0.7)' : '1px solid #f1f5f9',
                  background: action.urgent
                    ? dm ? 'rgba(127,29,29,0.15)' : 'rgba(254,242,242,0.6)'
                    : dm ? 'rgba(51,65,85,0.4)' : 'rgba(248,250,252,0.8)',
                }}
              >
                <div className={`rounded-lg p-2 shrink-0 ${dm ? 'bg-slate-600/60' : action.iconBg}`}>
                  <action.icon className={`h-4 w-4 ${action.iconColor}`} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className={`text-sm font-medium ${dm ? 'text-slate-200' : 'text-slate-800'}`}>{action.label}</p>
                  <p className={`text-xs ${dm ? 'text-slate-400' : 'text-slate-500'}`}>{loading ? '…' : action.sub}</p>
                </div>
                <ChevronRight className={`h-4 w-4 shrink-0 ${dm ? 'text-slate-500' : 'text-slate-400'}`} />
              </button>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* 24h Activity Heatmap */}
      <Card className={`border-0 shadow-sm ${dm ? 'bg-slate-800' : 'bg-white'}`}>
        <CardHeader className="pb-2 pt-5 px-5">
          <div className="flex items-center gap-2">
            <Activity className="h-4 w-4 text-blue-500" />
            <CardTitle className={`text-sm font-semibold ${dm ? 'text-slate-100' : 'text-slate-800'}`}>Last 24h Alert Activity</CardTitle>
            <span className={`text-xs font-normal ${dm ? 'text-slate-500' : 'text-slate-400'}`}>— per 2-hour window</span>
          </div>
        </CardHeader>
        <CardContent className="px-5 pb-5 pt-0">
          {loading ? (
            <div className="flex items-center justify-center py-6 text-slate-400">
              <RefreshCw className="animate-spin h-4 w-4 mr-2" />
              <span className="text-sm">Loading...</span>
            </div>
          ) : (
            <ActivityHeatmap alerts={allAlerts} dm={dm} />
          )}
        </CardContent>
      </Card>

      {/* At-Risk Students */}
      <Card className={`border-0 shadow-sm ${dm ? 'bg-slate-800' : 'bg-white'}`}>
        <CardHeader className="pb-2 pt-5 px-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangleIcon className="h-4 w-4 text-amber-500" />
              <CardTitle className={`text-sm font-semibold ${dm ? 'text-slate-100' : 'text-slate-800'}`}>Students Requiring Attention</CardTitle>
              <span className={`text-xs font-normal ${dm ? 'text-slate-500' : 'text-slate-400'}`}>— sorted by risk score</span>
            </div>
            <button
              onClick={() => setCurrentPage('studentsMonitored')}
              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium transition"
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
            >
              All Students <ChevronRight className="h-3 w-3" />
            </button>
          </div>
        </CardHeader>
        <CardContent className="px-5 pb-5 pt-0">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-slate-400">
              <RefreshCw className="animate-spin h-4 w-4 mr-2" />
              <span className="text-sm">Loading students...</span>
            </div>
          ) : atRiskStudents.length === 0 ? (
            <div className="flex items-center gap-3 py-6 justify-center text-slate-400">
              <Shield className="h-5 w-5 text-emerald-300" />
              <p className="text-sm">No students flagged — all good!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3">
              {atRiskStudents.map((student) => {
                const risk = riskColor(student.riskScore);
                const topCategory = studentAlertCounts(student.name);
                return (
                  <button
                    key={student.id}
                    onClick={() => setCurrentPage('studentsMonitored')}
                    className={`flex flex-col gap-2 p-4 rounded-xl hover:shadow-sm transition text-left ${dm ? 'hover:border-slate-600' : 'hover:border-slate-200'}`}
                    style={{
                      cursor: 'pointer',
                      background: dm ? 'rgba(30,41,59,0.6)' : '#ffffff',
                      border: dm ? '1px solid rgba(51,65,85,0.7)' : '1px solid #f1f5f9',
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${dm ? 'bg-slate-700 text-slate-300' : 'bg-slate-200 text-slate-600'}`}>
                          {student.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                        </div>
                        <p className={`text-sm font-medium truncate ${dm ? 'text-slate-200' : 'text-slate-700'}`}>{student.name}</p>
                      </div>
                      {/* Trend arrow */}
                      {risk.trend === 'up' && <TrendingUp className="h-3.5 w-3.5 text-red-400 shrink-0" />}
                      {risk.trend === 'stable' && <Minus className="h-3.5 w-3.5 text-amber-400 shrink-0" />}
                      {risk.trend === 'down' && <TrendingDown className="h-3.5 w-3.5 text-emerald-400 shrink-0" />}
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-xs font-semibold ${risk.text}`}>{risk.label}</span>
                        <span className="text-xs text-slate-500 font-medium">{student.riskScore}</span>
                      </div>
                      <div className={`w-full rounded-full h-1.5 ${dm ? 'bg-slate-700' : 'bg-slate-100'}`}>
                        <div
                          className={`h-1.5 rounded-full ${risk.bar} transition-all`}
                          style={{ width: `${Math.min(student.riskScore, 100)}%` }}
                        />
                      </div>
                    </div>
                    {topCategory && (
                      <p className={`text-xs truncate capitalize ${dm ? 'text-slate-500' : 'text-slate-400'}`}>{topCategory} this week</p>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

    </div>
  );
}

// Local icon to avoid import conflicts
function AlertTriangleIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

function Shield({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}
